import { useCallback } from 'react';
import { useMutation } from 'react-query';
import { InvoiceModel, PaymentOrigin } from '@frontend/api-invoices';
import { JustfiIntegration1 } from '@frontend/api-justifi-integration';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { UseCreatePaymentIntentSyncOptions, useInvoicePerson, useCreatePaymentIntentSync } from '../../../../../hooks';

export interface UseJustifiManualCardEntryPaymentOptions {
  cardName?: string;
  receiptEmail: string;
  paymentMethod?: PaymentMethod;
  invoice?: InvoiceModel;
  onInvoiceAlreadyPaid: UseCreatePaymentIntentSyncOptions['onInvoiceAlreadyPaid'];
  onRefetchInvoice: UseCreatePaymentIntentSyncOptions['onRefetchInvoice'];
}

export type MakeManualCardEntryPaymentProps = {
  paymentMethodId: string;
  paymentMethod: PaymentMethod;
};
export const useJustifiManualCardEntryPayment = ({
  cardName,
  receiptEmail,
  paymentMethod,
  invoice,
  onInvoiceAlreadyPaid,
  onRefetchInvoice,
}: UseJustifiManualCardEntryPaymentOptions) => {
  const locationId = invoice?.locationId;
  const personId = invoice?.person.id;
  const { personEmail: email } = useInvoicePerson(invoice);
  const amount = invoice?.billedAmount ?? 0;

  const { createPaymentIntent } = useCreatePaymentIntentSync({
    amount,
    email,
    personId,
    locationId,
    origin: PaymentOrigin.ManualCardEntry,
    setupFutureUsage: null,
    paymentMethod,
    invoiceId: invoice?.id,
    receiptEmail,
    cardholderName: cardName,
    onInvoiceAlreadyPaid,
    onRefetchInvoice,
  });

  const { mutateAsync: mutateCompleteCheckout } = JustfiIntegration1.Mutations.useCompleteCheckoutMutation();

  const createIntentAndCheckout = useCallback(
    async ({ paymentMethodId /* paymentMethod */ }: MakeManualCardEntryPaymentProps) => {
      const { paymentId, paymentIntent } = await createPaymentIntent({
        // paymentMethod, has payment method id
        // paymentMethod: { type: paymentMethod?.type }, // why can i not submit payment_id here?
      });

      if (!paymentId || !paymentIntent) {
        throw new Error('Failed to create payment intent');
      }

      return await mutateCompleteCheckout({
        locationId,
        paymentMethodId,
        idempotentId: invoice?.id,
        checkoutId: paymentIntent.id,
      });
    },
    [mutateCompleteCheckout, locationId, createPaymentIntent, invoice?.id]
  );

  const { mutateAsync: makeManualCardEntryPayment, isLoading: makingManualCardEntryPayment } = useMutation({
    mutationFn: async (props: MakeManualCardEntryPaymentProps) => createIntentAndCheckout(props),
  });
  return { makeManualCardEntryPayment, makingManualCardEntryPayment };
};
